import { authRequest } from '@/lib/api/auth-client';
import { HRM_PRODUCT_CODE } from '@/lib/api/config';
import type {
  CreateRoleInput,
  OrgRoleApiRow,
  OrgRoleHierarchyLevelRow,
  OrgRoleHierarchyMatrixRow,
  PlatformHierarchyLevel,
  ProductPermissionApiRow,
  RoleHierarchyMatrixResponse,
  UpdateRoleInput,
} from '@/types/role';

export async function listRoles(
  productCode: string = HRM_PRODUCT_CODE,
  options?: { functional?: boolean },
): Promise<OrgRoleApiRow[]> {
  const params = new URLSearchParams({ productCode });
  if (options?.functional === true) params.set('functional', 'true');
  if (options?.functional === false) params.set('functional', 'false');
  const envelope = await authRequest<OrgRoleApiRow[]>(
    `/roles?${params.toString()}`,
    { auth: true },
  );
  return envelope.data ?? [];
}

export async function listPlatformHierarchyLevels(): Promise<PlatformHierarchyLevel[]> {
  const envelope = await authRequest<PlatformHierarchyLevel[]>('/hierarchy-levels', {
    auth: true,
  });
  return envelope.data ?? [];
}

export async function getRoleHierarchyMatrix(
  productCode: string = HRM_PRODUCT_CODE,
): Promise<OrgRoleHierarchyMatrixRow[]> {
  const envelope = await authRequest<RoleHierarchyMatrixResponse>(
    `/roles/hierarchy-matrix?productCode=${encodeURIComponent(productCode)}`,
    { auth: true },
  );
  return envelope.data?.roles ?? [];
}

export async function createRoleHierarchyLevel(
  orgRoleId: string,
  input: { name: string; code?: string; rank?: number; description?: string },
): Promise<OrgRoleHierarchyLevelRow> {
  const envelope = await authRequest<OrgRoleHierarchyLevelRow>(
    `/roles/${encodeURIComponent(orgRoleId)}/hierarchy-levels`,
    {
      method: 'POST',
      auth: true,
      body: JSON.stringify(input),
    },
  );
  if (!envelope.data) throw new Error('Failed to create rank level');
  return envelope.data;
}

export async function updateRoleHierarchyLevel(
  levelId: string,
  input: { code?: string; name?: string; rank?: number; description?: string; isActive?: boolean },
): Promise<OrgRoleHierarchyLevelRow> {
  const envelope = await authRequest<OrgRoleHierarchyLevelRow>(
    `/roles/hierarchy-levels/${encodeURIComponent(levelId)}`,
    {
      method: 'PATCH',
      auth: true,
      body: JSON.stringify(input),
    },
  );
  if (!envelope.data) throw new Error('Failed to update rank level');
  return envelope.data;
}

export async function deleteRoleHierarchyLevel(levelId: string): Promise<void> {
  await authRequest<null>(`/roles/hierarchy-levels/${encodeURIComponent(levelId)}`, {
    method: 'DELETE',
    auth: true,
  });
}

export async function setRoleLevelPermissions(
  levelId: string,
  permissionCodes: string[],
): Promise<OrgRoleHierarchyLevelRow> {
  const envelope = await authRequest<OrgRoleHierarchyLevelRow>(
    `/roles/hierarchy-levels/${encodeURIComponent(levelId)}/permissions`,
    {
      method: 'PUT',
      auth: true,
      body: JSON.stringify({ permissionCodes }),
    },
  );
  if (!envelope.data) throw new Error('Failed to save rank permissions');
  return envelope.data;
}

export async function moveRoleHierarchyLevel(
  levelId: string,
  direction: 'up' | 'down',
): Promise<OrgRoleHierarchyLevelRow> {
  const envelope = await authRequest<OrgRoleHierarchyLevelRow>(
    `/roles/hierarchy-levels/${encodeURIComponent(levelId)}/move`,
    {
      method: 'POST',
      auth: true,
      body: JSON.stringify({ direction }),
    },
  );
  if (!envelope.data) throw new Error('Failed to reorder rank level');
  return envelope.data;
}

export async function listProductPermissions(
  productCode: string = HRM_PRODUCT_CODE,
): Promise<ProductPermissionApiRow[]> {
  const envelope = await authRequest<ProductPermissionApiRow[]>(
    `/products/${encodeURIComponent(productCode)}/permissions?isActive=true`,
    { auth: true },
  );
  return envelope.data ?? [];
}

export async function createRole(input: CreateRoleInput): Promise<OrgRoleApiRow> {
  const envelope = await authRequest<{ roleId: string; name: string }>('/roles', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(input),
  });
  const id = envelope.data?.roleId;
  if (!id) {
    throw new Error('Role created but no id was returned');
  }
  const roles = await listRoles(input.productCode);
  const created = roles.find((r) => r.id === id);
  if (!created) {
    throw new Error('Role created but could not be loaded');
  }
  return created;
}

export async function updateRole(
  roleId: string,
  input: UpdateRoleInput,
): Promise<OrgRoleApiRow> {
  const envelope = await authRequest<OrgRoleApiRow>(`/roles/${encodeURIComponent(roleId)}`, {
    method: 'PATCH',
    auth: true,
    body: JSON.stringify(input),
  });
  if (!envelope.data) {
    throw new Error('Update role returned no data');
  }
  return envelope.data;
}

export async function deleteRole(roleId: string): Promise<void> {
  await authRequest<null>(`/roles/${encodeURIComponent(roleId)}`, {
    method: 'DELETE',
    auth: true,
  });
}
